import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Party {
  id: number;
  name: string;
  date: string;
  total_amount?: number | string;
  contact_person_name?: string;
}

interface WithdrawalRecord {
  id: number;
  party_id: number;
  amount: number;
  date: string;
}

interface SellStockRecord {
  id: number;
  party_id: number;
  stock_name: string;
  sell_amount: string;
  sell_units: string;
  indirect_charges: string;
  date: string;
}

interface StockCategoryObj {
  id: number;
  stock_name: string;
  category: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  investment_type: string;
}

interface PartyStockDBType {
  id: number | string;
  party_id: number | string;
  categories: string | StockCategoryObj[];
  date?: string;
  date_of_purchase?: string;
}

interface FormulaChip {
  id: string;
  label: string;
  op: "+" | "−" | "×" | "÷";
}

interface ToggleConfig {
  withdrawal: boolean;
  sales: boolean;
  halve: boolean;
  use365: boolean;
  indirect: boolean;
}

interface RBResult {
  totalInterest: number;
  finalPrincipal: number;
  totalWithdrawal: number;
  days: number;
  steps: Array<{ label: string; desc: string; value: number; isWD?: boolean }>;
}

interface DCResult {
  totalInterest: number;
  finalPrincipal: number;
  initPrincipal: number;
  days: number;
  steps: Array<{ label: string; desc: string; value: number; isTx?: boolean }>;
}

type TabId = "inputs" | "results" | "formula" | "steps";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseCategoriesForDb(categories: string | StockCategoryObj[]): StockCategoryObj[] {
  if (Array.isArray(categories)) return categories;
  if (typeof categories === "string") {
    try {
      const v = categories.trim();
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        return Array.isArray(arr) ? arr : [arr];
      }
    } catch {}
  }
  return [];
}

const fmtINR = (v: number) =>
  "₹" + Math.round(v).toLocaleString("en-IN");

const fmtINRDecimal = (v: number) =>
  "₹" + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Formula Bank Defaults ────────────────────────────────────────────────────

const DEFAULT_BANK: FormulaChip[] = [
  { id: "principal", label: "Principal (P)", op: "+" },
  { id: "rate", label: "Rate (R)", op: "×" },
  { id: "days", label: "Days", op: "×" },
  { id: "divisor", label: "÷ 36500", op: "÷" },
  { id: "withdrawal", label: "Withdrawal", op: "−" },
  { id: "saleproceeds", label: "Sale proceeds", op: "+" },
  { id: "indirectcharges", label: "Indirect charges", op: "−" },
  { id: "halve", label: "÷ 2 (initial cap)", op: "÷" },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const InterestCalculator: React.FC = () => {
  // ── API state ──
  const [parties, setParties] = useState<Party[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRecord[]>([]);
  const [sellStocks, setSellStocks] = useState<SellStockRecord[]>([]);
  const [stocks, setStocks] = useState<PartyStockDBType[]>([]);
  const [interestRate, setInterestRate] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── User inputs ──
  const [selectedPartyId, setSelectedPartyId] = useState<string>("");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [manualRate, setManualRate] = useState<number | null>(null);

  // ── Tab ──
  const [activeTab, setActiveTab] = useState<TabId>("inputs");

  // ── Formula builder ──
  const [toggles, setToggles] = useState<ToggleConfig>({
    withdrawal: true,
    sales: true,
    halve: true,
    use365: false,
    indirect: false,
  });
  const [zoneChips, setZoneChips] = useState<FormulaChip[]>([]);
  const [bankChips, setBankChips] = useState<FormulaChip[]>(DEFAULT_BANK);
  const [customName, setCustomName] = useState("");
  const [customOp, setCustomOp] = useState<FormulaChip["op"]>("+");
  const dragBankItem = useRef<FormulaChip | null>(null);
  const dragZoneIdx = useRef<number | null>(null);

  // ── Results ──
  const [rbResult, setRbResult] = useState<RBResult | null>(null);
  const [dcResult, setDcResult] = useState<DCResult | null>(null);

  // ─── Fetch all data on mount ───────────────────────────────────────────────

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [partiesRes, wdRes, sellRes, stocksRes, settingsRes] = await Promise.all([
          fetch(`${apiUrl}/auth/addparty`).then((r) => r.json()).catch(() => []),
          fetch(`${apiUrl}/auth/withdrawals`).then((r) => r.json()).catch(() => []),
          fetch(`${apiUrl}/auth/sellstocks`).then((r) => r.json()).catch(() => []),
          fetch(`${apiUrl}/auth/addstock`).then((r) => r.json()).catch(() => []),
          axios.get(`${apiUrl}/auth/setting`).then((r) => r.data).catch(() => []),
        ]);

        if (Array.isArray(partiesRes)) setParties(partiesRes);
        if (Array.isArray(wdRes)) setWithdrawals(wdRes);
        if (Array.isArray(sellRes)) setSellStocks(sellRes);
        if (Array.isArray(stocksRes)) setStocks(stocksRes);

        if (Array.isArray(settingsRes)) {
          const pct = settingsRes.find((s: any) => s.meta_key === "percentage");
          if (pct) {
            const v = parseFloat(pct.meta_value);
            if (!isNaN(v)) setInterestRate(v * 12);
          }
        }
      } catch (e) {
        setError("Failed to load data from API.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // ─── Save/load formula config from localStorage ────────────────────────────

  useEffect(() => {
    try {
      const saved = localStorage.getItem("interestCalcFormulaConfig");
      if (saved) {
        const { toggles: t, zoneChips: z } = JSON.parse(saved);
        if (t) setToggles(t);
        if (z) {
          setZoneChips(z);
          const usedIds = new Set(z.map((c: FormulaChip) => c.id));
          setBankChips(DEFAULT_BANK.filter((c) => !usedIds.has(c.id)));
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem("interestCalcFormulaConfig", JSON.stringify({ toggles, zoneChips }));
  }, [toggles, zoneChips]);

  // ─── Calculations ──────────────────────────────────────────────────────────

  const effectiveRate = manualRate !== null ? manualRate : interestRate;

  const selectedParty = parties.find((p) => String(p.id) === selectedPartyId) || null;

  const calcRB = useCallback((): RBResult => {
    if (!selectedParty) return { totalInterest: 0, finalPrincipal: 0, totalWithdrawal: 0, days: 0, steps: [] };

    const principal = Number(selectedParty.total_amount || 0);
    const start = new Date(selectedParty.date || startDate);
    const end = new Date(endDate);
    const rate = effectiveRate;

    let P = principal;
    let totalInterest = 0;
    let lastDate = new Date(start);
    const steps: RBResult["steps"] = [];

    const partyWds = toggles.withdrawal
      ? withdrawals
          .filter((w) => String(w.party_id) === selectedPartyId)
          .map((w) => ({ date: new Date(w.date), amount: Number(w.amount) }))
          .filter((w) => w.date > start && w.date <= end)
          .sort((a, b) => a.date.getTime() - b.date.getTime())
      : [];

    const totalWD = partyWds.reduce((s, w) => s + w.amount, 0);

    partyWds.forEach((w, idx) => {
      const days = Math.floor((w.date.getTime() - lastDate.getTime()) / 86400000);
      if (days > 0) {
        const int = (P * rate / 36500) * days;
        totalInterest += int;
        steps.push({
          label: `Slice ${idx + 1}: before withdrawal`,
          desc: `₹${Math.round(P).toLocaleString("en-IN")} × ${rate}% ÷ 36500 × ${days} days`,
          value: int,
        });
      }
      steps.push({
        label: `Withdrawal on ${w.date.toLocaleDateString("en-IN")}`,
        desc: `Principal reduced by ₹${w.amount.toLocaleString("en-IN")}`,
        value: w.amount,
        isWD: true,
      });
      P -= w.amount;
      lastDate = new Date(w.date);
    });

    const finalDays = Math.floor((end.getTime() - lastDate.getTime()) / 86400000);
    if (finalDays > 0) {
      const int = (P * rate / 36500) * finalDays;
      totalInterest += int;
      steps.push({
        label: `Final slice`,
        desc: `₹${Math.round(P).toLocaleString("en-IN")} × ${rate}% ÷ 36500 × ${finalDays} days`,
        value: int,
      });
    }

    const days = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return { totalInterest, finalPrincipal: P, totalWithdrawal: totalWD, days, steps };
  }, [selectedParty, selectedPartyId, withdrawals, startDate, endDate, effectiveRate, toggles]);

  const calcDC = useCallback((): DCResult => {
    if (!selectedParty) return { totalInterest: 0, finalPrincipal: 0, initPrincipal: 0, days: 0, steps: [] };

    const principal = Number(selectedParty.total_amount || 0);
    const start = new Date(selectedParty.date || startDate);
    const end = new Date(endDate);
    const rate = effectiveRate;

    let P = toggles.halve ? principal / 2 : principal;
    const initPrincipal = P;
    let totalInterest = 0;
    let tempDate = new Date(start);
    const steps: DCResult["steps"] = [];

    const partyStocks = stocks.filter((s) => String(s.party_id) === selectedPartyId);
    const partySells = toggles.sales
      ? sellStocks.filter((s) => String(s.party_id) === selectedPartyId)
      : [];

    const soldNames = new Set(partySells.map((s) => s.stock_name));

    interface Tx {
      date: Date;
      amount: number;
      year: number;
      month: number;
      type: "purchase" | "sale";
    }

    const timeline: Tx[] = [];

    partyStocks.forEach((stk) => {
      const cats = parseCategoriesForDb(stk.categories);
      cats.forEach((cat) => {
        if (soldNames.has(cat.stock_name)) {
          const d = new Date(stk.date_of_purchase || stk.date || "");
          const amt = -(parseFloat(cat.total_amount) + parseFloat(cat.indirect_charges || "0"));
          timeline.push({ date: d, amount: amt, year: d.getFullYear(), month: d.getMonth(), type: "purchase" });
        }
      });
    });

    partySells.forEach((s) => {
      const d = new Date(s.date);
      const amt = parseFloat(s.sell_amount) - parseFloat(s.indirect_charges || "0");
      timeline.push({ date: d, amount: amt, year: d.getFullYear(), month: d.getMonth(), type: "sale" });
    });

    while (tempDate <= end) {
      const cy = tempDate.getFullYear();
      const cm = tempDate.getMonth();
      const lastDay = new Date(cy, cm + 1, 0);
      const endSlice = lastDay < end ? lastDay : end;
      const days = Math.ceil((endSlice.getTime() - tempDate.getTime()) / 86400000) + 1;

      if (days > 0) {
        const int = (P * rate / 36500) * days;
        totalInterest += int;
        steps.push({
          label: tempDate.toLocaleString("en-IN", { month: "long", year: "numeric" }),
          desc: `P = ₹${Math.round(P).toLocaleString("en-IN")}, Rate = ${rate}%, Days = ${days}`,
          value: int,
        });
      }

      const monthTxs = timeline.filter((t) => t.year === cy && t.month === cm);
      const delta = monthTxs.reduce((s, t) => s + t.amount, 0);
      if (delta !== 0) {
        steps.push({
          label: `Transaction adjustment — ${tempDate.toLocaleString("en-IN", { month: "short", year: "numeric" })}`,
          desc: `Net ${delta > 0 ? "+" : ""}₹${Math.abs(Math.round(delta)).toLocaleString("en-IN")} from ${monthTxs.length} transaction(s)`,
          value: delta,
          isTx: true,
        });
        P += delta;
      }

      tempDate = new Date(cy, cm + 1, 1);
    }

    const days = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return { totalInterest, finalPrincipal: P, initPrincipal, days, steps };
  }, [selectedParty, selectedPartyId, withdrawals, sellStocks, stocks, startDate, endDate, effectiveRate, toggles]);

  useEffect(() => {
    if (selectedPartyId) {
      setRbResult(calcRB());
      setDcResult(calcDC());
    }
  }, [selectedPartyId, calcRB, calcDC]);

  // ─── Drag & Drop ───────────────────────────────────────────────────────────

  const handleBankDragStart = (chip: FormulaChip) => {
    dragBankItem.current = chip;
  };

  const handleZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragBankItem.current) return;
    const chip = dragBankItem.current;
    setZoneChips((prev) => [...prev, chip]);
    setBankChips((prev) => prev.filter((c) => c.id !== chip.id));
    dragBankItem.current = null;
  };

  const handleZoneChipDragStart = (idx: number) => {
    dragZoneIdx.current = idx;
  };

  const handleZoneChipDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    const from = dragZoneIdx.current;
    if (from === null || from === targetIdx) return;
    setZoneChips((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(targetIdx, 0, moved);
      return next;
    });
    dragZoneIdx.current = null;
  };

  const removeFromZone = (idx: number) => {
    const chip = zoneChips[idx];
    setZoneChips((prev) => prev.filter((_, i) => i !== idx));
    if (DEFAULT_BANK.some((c) => c.id === chip.id)) {
      setBankChips((prev) => [...prev, chip]);
    }
  };

  const addCustomChip = () => {
    if (!customName.trim()) return;
    const chip: FormulaChip = { id: `custom_${Date.now()}`, label: customName.trim(), op: customOp };
    setBankChips((prev) => [...prev, chip]);
    setCustomName("");
  };

  // ─── Render helpers ────────────────────────────────────────────────────────

  const opColors: Record<string, string> = {
    "+": "rgba(29,158,117,0.15)",
    "−": "rgba(217,90,48,0.15)",
    "×": "rgba(24,95,165,0.15)",
    "÷": "rgba(127,119,221,0.15)",
  };
  const opTextColors: Record<string, string> = {
    "+": "#0F6E56",
    "−": "#993C1D",
    "×": "#185FA5",
    "÷": "#534AB7",
  };

  const OpBadge = ({ op }: { op: string }) => (
    <span
      style={{
        fontSize: 10,
        fontWeight: 500,
        padding: "2px 6px",
        borderRadius: 10,
        background: opColors[op] || "#eee",
        color: opTextColors[op] || "#333",
        marginRight: 4,
        flexShrink: 0,
      }}
    >
      {op}
    </span>
  );

  const Chip = ({
    chip,
    draggable,
    onDragStart,
    onRemove,
    onDrop,
    onDragOver,
  }: {
    chip: FormulaChip;
    draggable?: boolean;
    onDragStart?: () => void;
    onRemove?: () => void;
    onDrop?: (e: React.DragEvent) => void;
    onDragOver?: (e: React.DragEvent) => void;
  }) => (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDrop={onDrop}
      onDragOver={onDragOver ? (e) => { e.preventDefault(); onDragOver(e); } : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 20,
        border: "0.5px solid #d0d5dd",
        background: "#fff",
        fontSize: 12,
        cursor: draggable ? "grab" : "default",
        userSelect: "none",
        whiteSpace: "nowrap",
        gap: 4,
      }}
    >
      <OpBadge op={chip.op} />
      <span style={{ color: "#344054" }}>{chip.label}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          style={{
            marginLeft: 4,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#98a2b3",
            fontSize: 14,
            lineHeight: 1,
            padding: "0 2px",
          }}
        >
          ×
        </button>
      )}
    </div>
  );

  const Toggle = ({
    label,
    value,
    onChange,
    desc,
  }: {
    label: string;
    value: boolean;
    onChange: (v: boolean) => void;
    desc?: string;
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "0.5px solid #f2f4f7",
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: "#101828" }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: "#98a2b3", marginTop: 2 }}>{desc}</div>}
      </div>
      <button
        onClick={() => onChange(!value)}
        style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          background: value ? "#1D9E75" : "#d0d5dd",
          border: "none",
          cursor: "pointer",
          position: "relative",
          flexShrink: 0,
          transition: "background 0.2s",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: value ? 19 : 3,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.2s",
          }}
        />
      </button>
    </div>
  );

  const MetricCard = ({
    label,
    value,
    accent,
    sub,
  }: {
    label: string;
    value: string;
    accent?: string;
    sub?: string;
  }) => (
    <div
      style={{
        background: "#f9fafb",
        borderRadius: 8,
        padding: "12px 14px",
        border: "0.5px solid #f2f4f7",
      }}
    >
      <div style={{ fontSize: 11, color: "#98a2b3", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, color: accent || "#101828" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#98a2b3", marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const StepItem = ({
    num,
    label,
    desc,
    value,
    isWD,
    isTx,
  }: {
    num: number;
    label: string;
    desc: string;
    value: number;
    isWD?: boolean;
    isTx?: boolean;
  }) => {
    const color = isWD ? "#993C1D" : isTx ? (value > 0 ? "#185FA5" : "#993C1D") : "#0F6E56";
    const prefix = isWD ? "−" : isTx ? (value > 0 ? "+" : "") : "+";
    return (
      <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "#EEEDFE",
            color: "#3C3489",
            fontSize: 10,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          {num}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#101828" }}>{label}</div>
          <div style={{ fontSize: 11, color: "#98a2b3", marginTop: 2, fontFamily: "monospace" }}>{desc}</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color, minWidth: 80, textAlign: "right" }}>
          {prefix}{fmtINR(Math.abs(value))}
        </div>
      </div>
    );
  };

  // ─── Tab content ───────────────────────────────────────────────────────────

  const TabInputs = () => (
    <div>
      <Card title="Select party" style={{ marginBottom: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Label>Party</Label>
            <select
              value={selectedPartyId}
              onChange={(e) => setSelectedPartyId(e.target.value)}
              style={inputStyle}
            >
              <option value="">— Choose party —</option>
              {parties.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          {selectedParty && (
            <div>
              <Label>Capital (auto from party)</Label>
              <div style={{ ...inputStyle, background: "#f9fafb", color: "#344054", display: "flex", alignItems: "center" }}>
                {fmtINRDecimal(Number(selectedParty.total_amount || 0))}
              </div>
            </div>
          )}
        </div>

        {selectedParty && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
            <div>
              <Label>Start date (party join date)</Label>
              <input
                type="date"
                value={selectedParty.date?.split("T")[0] || startDate}
                disabled
                style={{ ...inputStyle, background: "#f9fafb", color: "#667085" }}
              />
            </div>
            <div>
              <Label>Interest end date</Label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <Label>
                Interest rate (% annual){" "}
                <span style={{ color: "#98a2b3", fontSize: 10 }}>
                  API: {interestRate}%
                </span>
              </Label>
              <input
                type="number"
                step="0.5"
                value={manualRate !== null ? manualRate : interestRate}
                onChange={(e) => setManualRate(parseFloat(e.target.value))}
                style={inputStyle}
              />
            </div>
          </div>
        )}
      </Card>

      {selectedParty && (
        <>
          <Card title="Withdrawals " style={{ marginBottom: 12 }}>
            {withdrawals.filter((w) => String(w.party_id) === selectedPartyId).length === 0 ? (
              <div style={{ fontSize: 13, color: "#98a2b3", padding: "8px 0" }}>No withdrawals found for this party.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "0.5px solid #f2f4f7" }}>
                    {["Date", "Amount", "Included"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#98a2b3", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {withdrawals
                    .filter((w) => String(w.party_id) === selectedPartyId)
                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                    .map((w) => (
                      <tr key={w.id} style={{ borderBottom: "0.5px solid #f9fafb" }}>
                        <td style={{ padding: "8px" }}>{new Date(w.date).toLocaleDateString("en-IN")}</td>
                        <td style={{ padding: "8px", fontWeight: 500 }}>{fmtINR(Number(w.amount))}</td>
                        <td style={{ padding: "8px" }}>
                          <span
                            style={{
                              fontSize: 11,
                              padding: "2px 8px",
                              borderRadius: 10,
                              background: toggles.withdrawal ? "#E1F5EE" : "#f2f4f7",
                              color: toggles.withdrawal ? "#0F6E56" : "#98a2b3",
                              fontWeight: 500,
                            }}
                          >
                            {toggles.withdrawal ? "Yes" : "No (toggle off)"}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Sell stocks ">
            {sellStocks.filter((s) => String(s.party_id) === selectedPartyId).length === 0 ? (
              <div style={{ fontSize: 13, color: "#98a2b3", padding: "8px 0" }}>No sell records found for this party.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "0.5px solid #f2f4f7" }}>
                    {["Stock", "Date", "Units", "Amount", "Charges"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#98a2b3", fontWeight: 500, fontSize: 11, textTransform: "uppercase" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sellStocks
                    .filter((s) => String(s.party_id) === selectedPartyId)
                    .map((s) => (
                      <tr key={s.id} style={{ borderBottom: "0.5px solid #f9fafb" }}>
                        <td style={{ padding: "8px", fontWeight: 500 }}>{s.stock_name}</td>
                        <td style={{ padding: "8px" }}>{new Date(s.date).toLocaleDateString("en-IN")}</td>
                        <td style={{ padding: "8px" }}>{s.sell_units}</td>
                        <td style={{ padding: "8px", color: "#0F6E56", fontWeight: 500 }}>{fmtINR(parseFloat(s.sell_amount))}</td>
                        <td style={{ padding: "8px", color: "#993C1D" }}>{fmtINR(parseFloat(s.indirect_charges || "0"))}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );

  const TabResults = () => {
    if (!rbResult || !dcResult || !selectedParty) {
      return (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#98a2b3" }}>
          Select a party from the Inputs tab to see results.
        </div>
      );
    }
    const diff = rbResult.totalInterest - dcResult.totalInterest;
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Card title="">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Reducing balance</h3>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#E1F5EE", color: "#0F6E56", fontWeight: 500 }}>
                On withdrawals
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <MetricCard label="Total interest" value={fmtINR(rbResult.totalInterest)} accent="#0F6E56" />
              <MetricCard label="Final principal" value={fmtINR(rbResult.finalPrincipal)} />
            </div>
            <MetricCard label="Total withdrawn" value={fmtINR(rbResult.totalWithdrawal)} accent="#854F0B" />
            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#98a2b3", background: "#f9fafb", borderRadius: 6, padding: "6px 10px", marginTop: 10 }}>
              (P × Rate / 36500) × Days, P reduces on each withdrawal
            </div>
          </Card>

          <Card title="">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Daily compounding</h3>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5", fontWeight: 500 }}>
                On buy/sell
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <MetricCard label="Total interest" value={fmtINR(dcResult.totalInterest)} accent="#0F6E56" />
              <MetricCard label="Final principal" value={fmtINR(dcResult.finalPrincipal)} />
            </div>
            <MetricCard
              label="Initial principal"
              value={fmtINR(dcResult.initPrincipal)}
              sub={toggles.halve ? "Capital ÷ 2" : "Full capital"}
            />
            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#98a2b3", background: "#f9fafb", borderRadius: 6, padding: "6px 10px", marginTop: 10 }}>
              (P × Rate / 36500) × Days, P adjusts monthly on buy/sell
            </div>
          </Card>
        </div>

        <Card title="Side-by-side comparison">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            <MetricCard label="RB interest" value={fmtINR(rbResult.totalInterest)} accent="#0F6E56" />
            <MetricCard label="DC interest" value={fmtINR(dcResult.totalInterest)} accent="#0F6E56" />
            <MetricCard
              label="Difference (RB − DC)"
              value={(diff >= 0 ? "+" : "") + fmtINR(Math.abs(diff))}
              accent={diff >= 0 ? "#0F6E56" : "#993C1D"}
            />
            <MetricCard label="Days elapsed" value={String(rbResult.days)} />
          </div>
        </Card>
      </div>
    );
  };

  const TabFormula = () => (
    <div>
      <Card title="Simple mode — toggle components" style={{ marginBottom: 12 }}>
        <Toggle
          label="Include withdrawals in principal reduction"
          desc="Reducing balance method only"
          value={toggles.withdrawal}
          onChange={(v) => setToggles((t) => ({ ...t, withdrawal: v }))}
        />
        <Toggle
          label="Include sale proceeds in principal increase"
          desc="Daily compounding method only"
          value={toggles.sales}
          onChange={(v) => setToggles((t) => ({ ...t, sales: v }))}
        />
        <Toggle
          label="Divide initial capital by 2"
          desc="Standard practice for daily compounding"
          value={toggles.halve}
          onChange={(v) => setToggles((t) => ({ ...t, halve: v }))}
        />
        <Toggle
          label="Use 365.25 days (accounts for leap years)"
          value={toggles.use365}
          onChange={(v) => setToggles((t) => ({ ...t, use365: v }))}
        />
        <Toggle
          label="Add indirect charges to cost base"
          value={toggles.indirect}
          onChange={(v) => setToggles((t) => ({ ...t, indirect: v }))}
        />
      </Card>

      <Card title="Advanced mode — drag to build formula">
        <div style={{ marginBottom: 8 }}>
          <Label>Formula zone — drag chips here, reorder by dragging</Label>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleZoneDrop}
            style={{
              minHeight: 56,
              border: "0.5px dashed #d0d5dd",
              borderRadius: 8,
              padding: 10,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
              background: "#f9fafb",
            }}
          >
            {zoneChips.length === 0 ? (
              <span style={{ fontSize: 12, color: "#98a2b3" }}>Drag components here to build formula</span>
            ) : (
              zoneChips.map((chip, i) => (
                <Chip
                  key={chip.id + i}
                  chip={chip}
                  draggable
                  onDragStart={() => handleZoneChipDragStart(i)}
                  onDrop={(e) => handleZoneChipDrop(e, i)}
                  onRemove={() => removeFromZone(i)}
                />
              ))
            )}
          </div>
          {zoneChips.length > 0 && (
            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#98a2b3", padding: "6px 2px" }}>
              = {zoneChips.map((c) => `${c.op} ${c.label}`).join(" ")}
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, marginBottom: 6 }}>
          <Label>Component bank — drag into formula zone</Label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {bankChips.map((chip) => (
              <Chip
                key={chip.id}
                chip={chip}
                draggable
                onDragStart={() => handleBankDragStart(chip)}
              />
            ))}
          </div>
        </div>

        <div style={{ height: "0.5px", background: "#f2f4f7", margin: "16px 0" }} />

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#667085" }}>Add custom component:</span>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="e.g. Tax deduction"
            style={{ ...inputStyle, width: 180 }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            {(["+", "−", "×", "÷"] as const).map((op) => (
              <button
                key={op}
                onClick={() => setCustomOp(op)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 12,
                  border: "0.5px solid",
                  borderColor: customOp === op ? "#AFA9EC" : "#d0d5dd",
                  background: customOp === op ? "#EEEDFE" : "transparent",
                  color: customOp === op ? "#3C3489" : "#667085",
                  fontSize: 12,
                  cursor: "pointer",
                  fontWeight: customOp === op ? 500 : 400,
                }}
              >
                {op}
              </button>
            ))}
          </div>
          <button onClick={addCustomChip} style={{ ...btnStyle }}>
            Add to bank
          </button>
        </div>
      </Card>
    </div>
  );

  const TabSteps = () => {
    if (!rbResult || !dcResult || !selectedParty) {
      return (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#98a2b3" }}>
          Select a party from the Inputs tab to see step-by-step breakdown.
        </div>
      );
    }
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Card title="">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Reducing balance — steps</h3>
            <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#E1F5EE", color: "#0F6E56", fontWeight: 500 }}>RB</span>
          </div>
          {rbResult.steps.map((s, i) => (
            <StepItem key={i} num={i + 1} label={s.label} desc={s.desc} value={s.value} isWD={s.isWD} />
          ))}
          <div style={{ borderTop: "0.5px solid #f2f4f7", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>Total interest</span>
            <span style={{ fontWeight: 500, fontSize: 13, color: "#0F6E56" }}>{fmtINRDecimal(rbResult.totalInterest)}</span>
          </div>
        </Card>

        <Card title="">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Daily compounding — steps</h3>
            <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 10, background: "#E6F1FB", color: "#185FA5", fontWeight: 500 }}>DC</span>
          </div>
          {dcResult.steps.map((s, i) => (
            <StepItem key={i} num={i + 1} label={s.label} desc={s.desc} value={s.value} isTx={s.isTx} />
          ))}
          <div style={{ borderTop: "0.5px solid #f2f4f7", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>Total interest</span>
            <span style={{ fontWeight: 500, fontSize: 13, color: "#0F6E56" }}>{fmtINRDecimal(dcResult.totalInterest)}</span>
          </div>
        </Card>
      </div>
    );
  };

  // ─── Shared style helpers ──────────────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "7px 10px",
    borderRadius: 8,
    border: "0.5px solid #d0d5dd",
    background: "#fff",
    color: "#101828",
    fontSize: 13,
    outline: "none",
  };

  const btnStyle: React.CSSProperties = {
    padding: "6px 14px",
    borderRadius: 8,
    border: "0.5px solid #d0d5dd",
    background: "transparent",
    color: "#344054",
    cursor: "pointer",
    fontSize: 13,
  };

  const Label = ({ children }: { children: React.ReactNode }) => (
    <label style={{ fontSize: 12, color: "#667085", display: "block", marginBottom: 4 }}>
      {children}
    </label>
  );

  const Card = ({
    title,
    children,
    style,
  }: {
    title: string;
    children: React.ReactNode;
    style?: React.CSSProperties;
  }) => (
    <div
      style={{
        background: "#fff",
        border: "0.5px solid #e4e7ec",
        borderRadius: 12,
        padding: "16px 20px",
        ...style,
      }}
    >
      {title && (
        <h3 style={{ fontSize: 15, fontWeight: 500, margin: "0 0 14px 0", color: "#101828" }}>
          {title}
        </h3>
      )}
      {children}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          flexDirection: "column",
          gap: 12,
          color: "#98a2b3",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: "2px solid #EEEDFE",
            borderTop: "2px solid #7F77DD",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span style={{ fontSize: 14 }}>Loading data from API…</span>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: "#993C1D", background: "#FAECE7", borderRadius: 12, margin: 24 }}>
        {error}
      </div>
    );
  }

  const tabs: { id: TabId; label: string }[] = [
    { id: "inputs", label: "Inputs" },
    { id: "results", label: "Results" },
    { id: "formula", label: "Formula builder" },
    { id: "steps", label: "Step-by-step" },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px 0", color: "#101828" }}>
            Interest calculator
          </h1>
          <p style={{ fontSize: 13, color: "#667085", margin: 0 }}>
            Compare reducing balance vs daily compounding — step-by-step, with configurable formula
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {rbResult && dcResult && (
            <>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#98a2b3" }}>RB interest</div>
                <div style={{ fontSize: 16, fontWeight: 500, color: "#0F6E56" }}>{fmtINR(rbResult.totalInterest)}</div>
              </div>
              <div style={{ width: "0.5px", background: "#f2f4f7", margin: "0 4px" }} />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 11, color: "#98a2b3" }}>DC interest</div>
                <div style={{ fontSize: 16, fontWeight: 500, color: "#185FA5" }}>{fmtINR(dcResult.totalInterest)}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              border: "0.5px solid",
              borderColor: activeTab === t.id ? "#AFA9EC" : "#d0d5dd",
              background: activeTab === t.id ? "#EEEDFE" : "transparent",
              color: activeTab === t.id ? "#3C3489" : "#667085",
              fontSize: 13,
              fontWeight: activeTab === t.id ? 500 : 400,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "inputs" && <TabInputs />}
      {activeTab === "results" && <TabResults />}
      {activeTab === "formula" && <TabFormula />}
      {activeTab === "steps" && <TabSteps />}
    </div>
  );
};

export default InterestCalculator;